import React from "react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import Marquee from "../components/sections/Marquee";
import SectionDivider from "../components/SectionDivider";
import RevealOnScroll from "../components/RevealOnScroll";
import {
    ModalityHero, ProblemSection, HowItHelpsSection,
    ThePracticeSection, BeginTogetherSection,
    IssuesListSection, FAQSection,
} from "./modalityShared";

const issues = [
    "Emotional overwhelm or difficulty calming down after conflict",
    "Impulsive reactions you later regret",
    "Chronic feelings of emptiness or shame",
    "Difficulty in close relationships — fear of abandonment, intensity, or shutdown",
    "Self-critical thinking that is relentless and hard to quiet",
    "Anxiety that spikes quickly and feels hard to manage",
    "Difficulty tolerating uncertainty or distress without acting out",
];

const faqs = [
    { q: "Is DBT only for people with borderline personality disorder?", a: "No. While DBT was originally developed for BPD, the skills are highly effective for anyone who struggles with emotional intensity, impulsivity, conflict in relationships, or difficulty tolerating distress. It is used widely for anxiety, depression, trauma, and life transitions." },
    { q: "Is DBT very structured or regimented?", a: "At Alchemy Practice, DBT skills are integrated into individual therapy in a way that is responsive to your needs — not a rigid curriculum. You get the benefit of the skills without a one-size-fits-all format." },
    { q: "What are the four DBT skill areas?", a: "Mindfulness, distress tolerance, emotion regulation, and interpersonal effectiveness. Each area builds a specific kind of capacity that supports the others." },
    { q: "Will I have homework or assignments?", a: "Some clients find it helpful to practice skills between sessions, and I may suggest specific exercises when they feel relevant. But there is no rigid homework structure. The goal is for these tools to feel natural and usable, not like another obligation." },
];

const steps = [
    { n: "I", title: "Understanding Your Emotional Patterns", body: "We start by mapping what happens for you when emotions escalate — the triggers, the cycle, the aftermath. Understanding your pattern is the first step toward being able to interrupt it. You learn to recognize what is happening before it takes over." },
    { n: "II", title: "Building Your Skills Toolkit", body: "We introduce DBT skills as they become relevant to what you are navigating. Each skill is practiced in the context of your real experiences, so they feel immediately useful — not theoretical. Over time, they become part of how you move through the world." },
    { n: "III", title: "Integration and Lasting Change", body: "As the skills become more natural, we focus on applying them in the areas that matter most: your relationships, your self-talk, your ability to tolerate discomfort without shutting down or lashing out. This is where the work becomes genuinely life-changing." },
];

const phrases = [
    "You are not too much. Your emotions are information, not problems.",
    "DBT is not about becoming less emotional. It is about becoming more capable.",
    "Steadiness is not the absence of intensity. It is knowing what to do with it.",
    "The skills do not change who you are. They give you more choice in how you show up.",
];

export default function DBTPage() {
    return (
        <main className="bg-ivory text-charcoal">
            <Seo
                title="DBT Therapy in Wisconsin & Colorado | Alchemy Practice"
                description="DBT therapy for emotional overwhelm and self-doubt. Practical skills for managing intense emotions. Virtual telehealth sessions in Wisconsin and Colorado."
            />
            <Nav />
            <RevealOnScroll>
                <ModalityHero
                    testidPrefix="dbt"
                    eyebrow="Dialectical Behavior Therapy"
                    headline="DBT Therapy for Emotional Overwhelm, Anxiety, and"
                    italic="Self-Doubt"
                    subhead="You understand your emotions. You just do not always know how to manage them."
                />
                <SectionDivider color="gold" bg="#FAFAF8" />
                <ProblemSection
                    testidPrefix="dbt"
                    eyebrow="What Brings You Here"
                    headline="When Emotions Feel Bigger"
                    italic="Than You Want Them To"
                    firstLine="You may feel emotionally overwhelmed more easily than other people."
                    paragraphs={[
                        "You replay situations in your head. You become consumed by anxiety, shame, fear, or self-criticism. You may react strongly emotionally and later wish you had handled things differently.",
                        "Even when you understand why you react the way you do, it can still feel difficult to change those reactions in the moment.",
                        "This can become exhausting, especially for people who feel deeply and hold themselves to high expectations.",
                    ]}
                />
                <SectionDivider color="sage" bg="#FAFAF8" />
                <HowItHelpsSection
                    testidPrefix="dbt"
                    eyebrow="The Approach"
                    headline="How DBT"
                    italic="Helps"
                    paragraphs={[
                        "Dialectical Behavior Therapy provides practical, concrete skills for navigating emotions, tolerating stress, improving relationships, and responding differently when anxiety or self-doubt take over.",
                        "DBT combines emotional validation with real change. You do not have to judge yourself for struggling while also learning how to respond differently moving forward.",
                        "Over time, many clients feel more stable, confident, emotionally regulated, and able to navigate difficult situations without becoming overwhelmed by them.",
                    ]}
                    quote={'"What I appreciate most about DBT is that clients often begin using the skills immediately in their everyday lives. Many people feel relieved to finally have tools that help them feel more emotionally grounded and in control of their reactions."'}
                />
                <SectionDivider color="rose" bg="#EEF2ED" />
                <IssuesListSection
                    testidPrefix="dbt"
                    eyebrow="What DBT Helps With"
                    headline="When emotions feel like they are"
                    italic="running the show."
                    items={issues}
                />
                <SectionDivider color="gold" bg="#FAFAF8" />
                <FAQSection
                    testidPrefix="dbt"
                    heading="Questions About"
                    italic="DBT"
                    faqs={faqs}
                />
                <SectionDivider color="rose" bg="#EEF2ED" />
                <ThePracticeSection
                    testidPrefix="dbt"
                    eyebrow="At Alchemy Practice"
                    headline="A Boutique, Relationship-Centered"
                    italic="Approach"
                    paragraphs={[
                        "At Alchemy Practice, DBT is integrated within a thoughtful, personalized therapy process rather than delivered mechanically.",
                        "The work is collaborative, emotionally attuned, and designed to help clients create meaningful, lasting change rather than simply learning coping skills in isolation.",
                    ]}
                />
                <SectionDivider color="gold" bg="#F2EDE6" />
                <BeginTogetherSection steps={steps} testidPrefix="dbt" />
                <Marquee phrases={phrases} />
                <Footer />
            </RevealOnScroll>
        </main>
    );
}
